import { useState } from 'react';
import { api, setToken } from '../api.js';

/**
 * Accepting an invitation, and completing a password reset.
 *
 * Both arrive as a signed token in the URL FRAGMENT rather than the query
 * string, so the token never reaches the server in a request line and never
 * lands in an access log or a Referer header.
 */
export function SetPassword({ mode, token, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const invite = mode === 'invite';
  const mismatch = confirm.length > 0 && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post(invite ? '/api/users/accept-invite' : '/api/users/reset',
        { token, password });
      setToken(r.token);
      onDone();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <h1 className="login__brand">Ledger</h1>
        <p className="login__tag">
          {invite ? 'Choose a password and you are in.' : 'Choose a new password.'}
        </p>

        {err && (
          <div className="error">
            {err}
            <div style={{ marginTop: 8 }}>
              <button type="button" className="btn btn--quiet" onClick={onDone}>Back to sign in</button>
            </div>
          </div>
        )}

        <label className="field"><span>New password</span>
          <input type="password" value={password} minLength={12} required autoComplete="new-password"
                 onChange={(e) => setPassword(e.target.value)} /></label>
        <div style={{ fontSize: 12, color: 'var(--ink-faint)', marginTop: -8, marginBottom: 12 }}>
          At least 12 characters.
        </div>

        <label className="field"><span>Confirm password</span>
          <input type="password" value={confirm} required autoComplete="new-password"
                 onChange={(e) => setConfirm(e.target.value)} /></label>
        {mismatch && <div style={{ fontSize: 12, color: 'var(--unbalanced)', marginTop: -8 }}>
          Those do not match.
        </div>}

        <button className="btn" type="submit" disabled={busy || mismatch || password.length < 12}>
          {busy ? 'Saving' : invite ? 'Set password and sign in' : 'Change password'}
        </button>
      </form>
    </div>
  );
}

/** Ask for a reset link. Always answers the same, so it cannot enumerate accounts. */
export function ForgotPassword({ onBack }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post('/api/users/request-reset', { email }); } catch { /* answer is the same either way */ }
    setSent(true); setBusy(false);
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <h1 className="login__brand">Ledger</h1>
        <p className="login__tag">Reset your password.</p>

        {sent ? (
          <>
            <div className="ok">
              If <strong>{email}</strong> has an account, a reset link is on its way. It expires in
              two hours.
            </div>
            <button className="btn" type="button" onClick={onBack}>Back to sign in</button>
          </>
        ) : (
          <>
            <label className="field"><span>Email</span>
              <input type="email" value={email} required autoComplete="email"
                     onChange={(e) => setEmail(e.target.value)} /></label>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Sending' : 'Send a reset link'}
            </button>
            <div className="login__switch">
              <button type="button" onClick={onBack}>Back to sign in</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
